/**
 * @file TenantManagement.js
 * @description Main page for managing tenants/admins. Provides list view, filtering, and detailed drilling.
 */
//Tenantmanagement.js

import React, { useEffect, useState } from "react";
import NewTenantProvisioningModal from "../components/NewTenantProvisioningModal";
import CreateUserModal from "../components/CreateUserModal";
import LoadingOverlay from "../components/LoadingOverlay";
import Chart from "react-apexcharts";
import {
  fetchTenantOverview,
  fetchUserDetailedMetrics,
  getTenantList,
} from "../services/tenantService";
import { getAvailablePlans } from "../services/analyticsService";
import TenantDetailModal from "../components/TenantDetailModal";
import UserDetailModal from "../components/UserDetailModal";
import ScrollableTable from "../components/ScrollableTable";
import {
  FaSpinner,
  FaBuilding,
  FaUserShield,
  FaChartPie,
  FaSync,
  FaExpand,
  FaPlus,
  FaChevronLeft,
  FaChevronRight
} from "react-icons/fa";
import TimeFilter from "../components/TimeFilter";
import ExpandedChartModal from "../components/ExpandedChartModal";
import InfoTooltip from "../components/InfoTooltip";
import { useTimeFilter } from "../context/TimeFilterContext";
import { getNodeTitle } from "../utils/nodeMappings";

/**
 * Tenant Management Page Component.
 * Displays charts for high-level metrics and a searchable table of tenants.
 */
export default function TenantManagement() {
  const { timeFilter, setTimeFilter, customRange, handleCustomRangeChange,
    planFilter, setPlanFilter,
    availablePlans, setAvailablePlans
  } = useTimeFilter();
  const [data, setData] = useState(null);
  const [tenantList, setTenantList] = useState([]);
  const [activeScope, setActiveScope] = useState("estrelSaas");
  const [loading, setLoading] = useState(true); // Unified loading state
  const [expandedChart, setExpandedChart] = useState(null);

  const handleExpandChart = (title, options, series, type) => {
    setExpandedChart({ title, options, series, type });
  };

  const [tenantModal, setTenantModal] = useState({ show: false, data: null });
  const [userModal, setUserModal] = useState({ show: false, data: null });
  const [showCreateUser, setShowCreateUser] = useState(false);

  // Pagination
  const [tenantPage, setTenantPage] = useState(1);
  const itemsPerPage = 10;

  // Search State
  const [searchQuery, setSearchQuery] = useState("");

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' }); // Default sort by name

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
    if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;
    getAvailablePlans(timeFilter, customRange).then(setAvailablePlans);
  }, [timeFilter, customRange, setAvailablePlans]);

  const loadData = (force = false) => {
    if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
    if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;

    setLoading(true);

    Promise.all([
      fetchTenantOverview(timeFilter, customRange, planFilter, force),
      getTenantList(activeScope, timeFilter, customRange, force)
    ]).then(([overviewData, listData]) => {
      if (overviewData) setData(overviewData);
      if (listData) setTenantList(listData);

      setTimeout(() => {
        setLoading(false);
      }, 600);
    }).catch(err => {
      console.error("Failed to load tenant data", err);
      setLoading(false);
    });
  };

  useEffect(() => {
    loadData();
  }, [timeFilter, customRange, planFilter, activeScope]);

  // Derive filtered list based on plan AND search query
  const filteredTenantList = React.useMemo(() => {
    let filtered = tenantList;

    // Filter by Plan
    if (planFilter && planFilter !== "all") {
      filtered = filtered.filter((t) => t.plan === planFilter);
    }

    // Filter by Search Query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((t) =>
        Object.values(t).some((val) =>
          String(val).toLowerCase().includes(q)
        )
      );
    }

    return filtered;
  }, [tenantList, planFilter, searchQuery]);

  // Apply Sorting
  const sortedTenantList = React.useMemo(() => {
    if (!sortConfig.key) return filteredTenantList;
    return [...filteredTenantList].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Assuming numeric for some fields or string for others
      if (['usersCount', 'executions', 'storageGB', 'workflows', 'projects'].includes(sortConfig.key)) {
        aVal = parseInt(aVal, 10) || 0;
        bVal = parseInt(bVal, 10) || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredTenantList, sortConfig]);

  if (!data && loading)
    return (
      <div className="p-4 bg-light min-vh-100 position-relative">
        <LoadingOverlay loading={true} message="Loading Tenant Overview..." />
      </div>
    );

  if (!data)
    return (
      <div className="p-5 text-center text-danger">
        Error: Could not retrieve tenant data.
      </div>
    );

  /**
   * Opens the detail modal for a specific tenant row.
   * Fetches detailed metrics before showing the modal.
   * @param {Object} row - The tenant object from the table.
   */
  const handleViewDetailsClick = async (row) => {
    if (loading) return;
    setLoading(true);
    const detailedData = await fetchUserDetailedMetrics(row.username, timeFilter, customRange);
    setLoading(false);
    if (detailedData) setTenantModal({ show: true, data: detailedData });
    else alert("Could not fetch details for this tenant.");
  };

  // --- CHART OPTIONS (Styled to match Dashboard) ---
  const commonChartOptions = {
    chart: { toolbar: { show: false }, parentHeightOffset: 0 },
    dataLabels: { enabled: false },
  };

  const planOptions = {
    ...commonChartOptions,
    xaxis: { categories: data.charts?.usersByPlan?.labels || [] },
    colors: ["#AD2D45", "#912059", "#E68F17", "#F4D677"],
    plotOptions: {
      bar: { borderRadius: 6, distributed: true, columnWidth: "50%" },
    },
    legend: { show: false },
  };

  const execOptions = {
    ...commonChartOptions,
    stroke: { curve: "smooth", width: 3 },
    xaxis: { categories: data.charts?.executionTrend?.categories || [] },
    colors: ["#AD2D45"],
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05 },
    },
    markers: { size: 4, hover: { size: 6 } }, // Ensure points are visible
  };

  const moduleOptions = {
    ...commonChartOptions,
    labels: (data.charts?.moduleUsage?.labels || []).map(getNodeTitle),
    colors: ["#E68F17", "#FBC70F", "#912059"],
    legend: { position: "bottom" },
    plotOptions: { pie: { donut: { size: "75%" } } },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        return opts.w.config.series[opts.seriesIndex];
      },
      style: {
        fontSize: '12px',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontWeight: 'bold',
        colors: ['#fff']
      },
      dropShadow: {
        enabled: true,
      }
    },
  };

  const columns = [
    {
      header: "Admin Name",
      accessor: "name",
      render: (v) => <span className="text-dark">{v}</span>,
    },
    {
      header: "Plan",
      accessor: "plan",
      render: (v) => {
        const colors = {
          starter: "bg-primary-subtle text-primary",
          education: "bg-info-subtle text-info",
          basic: "bg-success-subtle text-success",
          enterprise: "bg-purple-subtle text-purple", // Custom CSS needed or use hex
        };
        return (
          <span
            className={`badge border-0 text-capitalize px-3 py-2 ${colors[v] || "bg-light text-muted"
              }`}
          >
            {v || "Free"}
          </span>
        );
      },
    },
    {
      header: "Subscription Start",
      accessor: "subscriptionStartDate",
      render: (v) => <span className="text-secondary small">{v}</span>,
    },
    {
      header: "Subscription End",
      accessor: "subscriptionEndDate",
      render: (v) => <span className="text-secondary small">{v}</span>,
    },
    {
      header: "Collaborators",
      accessor: "usersCount",
      render: (v) => (
        <span className="badge bg-light text-dark fw-medium">{v} Users</span>
      ),
    },
    {
      header: "Actions",
      accessor: "id",
      render: (v, row) => (
        <button
          className="btn btn-sm btn-white border shadow-sm px-3"
          onClick={(e) => {
            e.stopPropagation();
            handleViewDetailsClick(row);
          }}
          disabled={loading}
        >
          View Details
        </button>
      ),
    },
  ];




  return (
    <div className="p-4 bg-light min-vh-100 position-relative">
      <LoadingOverlay loading={loading} />
      {/* HEADER SECTION */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold text-dark mb-1">Tenant Management</h3>
          <p className="text-muted small">
            Manage organizations, monitor plan distribution, and review tenant
            activity.
          </p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm shadow-sm border-secondary-subtle"
            style={{ width: "130px", fontWeight: "500" }}
            value={planFilter || ""}
            onChange={(e) =>
              setPlanFilter(e.target.value === "" ? null : e.target.value)
            }
          >
            <option value="">All Plans</option>
            {availablePlans.map((p) => (
              <option key={p} value={p}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </option>
            ))}
          </select>
          <TimeFilter
            value={timeFilter}
            onChange={setTimeFilter}
            startDate={customRange.start}
            endDate={customRange.end}
            onCustomRangeChange={handleCustomRangeChange}
          />
          <div className="text-end d-none d-md-block ms-2">
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>Last Update</div>
            <div className="fw-bold small">{new Date().toLocaleTimeString()}</div>
          </div>
          <button className="btn btn-white border shadow-sm btn-sm px-3" onClick={() => loadData(true)}>
            <FaSync className={loading ? "fa-spin me-2" : "me-2"} /> Refresh
          </button>
          {/* <button className="btn btn-primary shadow-sm btn-sm px-3 fw-bold" onClick={() => setShowCreateUser(true)}>
            <FaPlus className="me-2" /> New User
          </button> */}
        </div>
      </div>

      {/* SCOPE SWITCHER (Modern Tabs) */}
      <div className="d-flex gap-2 mb-4">
        {data?.scopes?.map((s) => (
          <button
            key={s.id}
            className={`btn btn-sm px-4 py-2 rounded-pill fw-medium transition-all ${activeScope === s.id
              ? "bg-dark text-white shadow-sm"
              : "bg-white text-muted border"
              }`}
            onClick={() => !s.disabled && setActiveScope(s.id)}
            disabled={s.disabled}
            style={{ borderRadius: "6px", minWidth: "140px" }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* ANALYTICS ROW */}
      <div className="row g-4 mb-4">
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="d-flex align-items-center gap-2 mb-3">
              <FaUserShield className="text-primary" />
              <h6 className="fw-bold mb-0 text-uppercase">Subscribers by Plan <InfoTooltip text="Distribution of users across different subscription plans." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Subscribers by Plan", planOptions, data?.charts?.usersByPlan?.series, "bar")} style={{ marginLeft: "auto" }}>
                <FaExpand />
              </button>
            </div>
            {data?.charts?.usersByPlan?.series?.[0]?.data?.length > 0 ? (
              <Chart
                options={planOptions}
                series={data?.charts?.usersByPlan?.series}
                type="bar"
                height={220}
              />
            ) : (
              <div className="text-center py-5 text-muted small">
                No data for this scope
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="d-flex align-items-center gap-2 mb-3">
              <FaChartPie className="text-success" />
              <h6 className="fw-bold mb-0 text-uppercase">Assets Count<InfoTooltip text="Usage volume for different platform modules." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Assets Count", moduleOptions, data?.charts?.moduleUsage?.series, "donut")} style={{ marginLeft: "auto" }}>
                <FaExpand />
              </button>
            </div>

            {data?.charts?.moduleUsage?.series?.some((v) => v > 0) ? (
              <Chart
                options={moduleOptions}
                series={data?.charts?.moduleUsage?.series}
                type="donut"
                height={220}
              />
            ) : (
              <div className="text-center py-5 text-muted small">
                No module usage recorded
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-2">
                <FaBuilding className="text-warning" />
                <h6 className="fw-bold mb-0 text-uppercase">Execution Trend <InfoTooltip text="Trend of workflow executions over the selected period." /></h6>
              </div>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Execution Trend", execOptions, data?.charts?.executionTrend?.series, "area")}>
                <FaExpand />
              </button>
            </div>
            {data?.charts?.executionTrend?.series?.[0]?.data?.length > 0 ? (
              <Chart
                options={execOptions}
                series={data?.charts?.executionTrend?.series}
                type="area"
                height={220}
              />
            ) : (
              <div className="text-center py-5 text-muted small">
                No execution data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MAIN TABLE SECTION */}
      <div className="card border-0 shadow-sm rounded-3">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <h6 className="fw-bold mb-0 text-uppercase">{`${activeScope === "estrelSaas" ? "Estrel SaaS" : activeScope} Organization Admins`}</h6>
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center gap-1 bg-light rounded px-2 py-1">
              <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setTenantPage(prev => Math.max(prev - 1, 1))} disabled={tenantPage === 1} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                <FaChevronLeft size={12} />
              </button>
              <span className="small text-muted mx-2" style={{ fontSize: '0.8rem', minWidth: '40px', textAlign: 'center' }}>
                {tenantPage} / {Math.ceil(sortedTenantList.length / itemsPerPage) || 1}
              </span>
              <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setTenantPage(prev => Math.min(prev + 1, Math.ceil(sortedTenantList.length / itemsPerPage)))} disabled={tenantPage >= Math.ceil(sortedTenantList.length / itemsPerPage)} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                <FaChevronRight size={12} />
              </button>
            </div>
            <div className="ms-3 border-start ps-3" style={{ minWidth: "250px" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by name, email or org..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setTenantPage(1); }}
              />
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <FaSpinner className="fa-spin text-primary mb-2" size={30} />
              <div className="text-muted">Synchronizing tenant list...</div>
            </div>
          ) : (
            <ScrollableTable
              title=""
              data={sortedTenantList.slice((tenantPage - 1) * itemsPerPage, tenantPage * itemsPerPage)}
              columns={columns}
              searchPlaceholder="Search by name, email or org..."
              height={500}
              sortConfig={sortConfig}
              onSort={handleSort}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              hideSearch={true}
            />
          )}
        </div>
      </div>

      {/* MODALS */}
      <TenantDetailModal
        show={tenantModal.show}
        data={tenantModal.data}
        onClose={() => setTenantModal({ show: false, data: null })}
      />
      <UserDetailModal
        show={userModal.show}
        data={userModal.data}
        onClose={() => setUserModal({ show: false, data: null })}
      />
      <ExpandedChartModal
        show={!!expandedChart}
        onClose={() => setExpandedChart(null)}
        {...expandedChart}
      />
      <CreateUserModal
        show={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        onSuccess={() => {
          // Refresh list on success
          setLoading(true);
          getTenantList(activeScope, timeFilter).then((list) => {
            setTenantList(list);
            setLoading(false);
          });
        }}
      />
    </div >
  );
}
